/**
 * Store-switch helpers (Business Rule #50 — a store switch carries the cart).
 *
 * The switch itself lives in CartContext (`switchBranch`) because it owns the
 * cart + its sync path. What lives here is the pure, unit-tested copy that
 * every surface (Home toast, Cart banner) renders from the switch result, so
 * the wording can never drift between entry points.
 */

/**
 * Build the one-line summary shown after a store switch.
 *
 * @param {{ branch: {name?: string}, changed?: boolean, moved?: Array<{name: string}>,
 *          dropped?: Array<{name: string, reason?: string}>, offersRemoved?: string[],
 *          cleared?: boolean, hadCart?: boolean }} result — the value `switchBranch` resolves to.
 * @returns {string} e.g. "2 items moved to Nahar. Croissant isn't available there."
 */
export function buildSwitchSummary(result) {
  const name = (result?.branch?.name || '').trim() || 'the new store';
  const moved = result?.moved || [];
  const dropped = result?.dropped || [];
  const offersRemoved = result?.offersRemoved || [];

  if (result?.cleared) {
    return `Switched to ${name}. We couldn't move your cart, so it was cleared.`;
  }
  if (!result?.hadCart) {
    return `Switched to ${name}.`;
  }
  if (moved.length === 0) {
    return `None of your items are available at ${name}.`;
  }

  const parts = [`${moved.length} ${moved.length === 1 ? 'item' : 'items'} moved to ${name}.`];
  if (dropped.length > 0) {
    parts.push(`${joinNames(dropped.map(d => d.name))} ${dropped.length === 1 ? "isn't" : "aren't"} available there.`);
  }
  if (offersRemoved.length > 0) {
    parts.push(`Today's offer on ${joinNames(offersRemoved)} doesn't apply there.`);
  }
  return parts.join(' ');
}

/**
 * The note after the Cart / Checkout repair moved another store's lines onto
 * the current store (Business Rule #50). Re-pricing is SILENT (owner,
 * 2026-10-14) — the customer only hears about a line this store doesn't sell,
 * because an item vanishing from the cart unexplained is worse than a note.
 *
 * @param {{ branch: {name?: string}, dropped?: Array<{name: string}> }} result —
 *          what `CartContext.repairCartStore` resolves to.
 * @returns {string} e.g. "Croissant isn't available at Gigaplex Airoli, so it
 *          was removed." ('' when nothing was removed)
 */
export function buildStoreRepairSummary(result) {
  const name = (result?.branch?.name || '').trim() || 'this store';
  const dropped = uniqueNames((result?.dropped || []).map(d => d.name));
  if (dropped.length === 0) return '';
  return `${joinNames(dropped)} ${dropped.length === 1 ? "isn't" : "aren't"} available at ${name}, so ${dropped.length === 1 ? 'it was' : 'they were'} removed.`;
}

/**
 * Cart lines whose `menu_item_id` isn't among `menuRows` — the branch-scoped
 * `/menu/items/batch` result — i.e. lines from another store's menu (or deleted
 * rows). Empty when every line belongs to the current store.
 * @param {Array<{menu_item_id?: string}>} cart
 * @param {Array<{id: string}>} menuRows
 */
export function foreignCartLines(cart, menuRows) {
  const known = new Set((menuRows || []).map(m => m.id));
  return (cart || []).filter(c => c.menu_item_id && !known.has(c.menu_item_id));
}

function uniqueNames(names) {
  return [...new Set((names || []).filter(Boolean))];
}

/**
 * "A", "A and B", "A, B and C" — the short human list used in the summary.
 * @param {string[]} names
 */
export function joinNames(names) {
  const list = (names || []).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** The saved store, or null. Shared reader so callers don't re-parse localStorage by hand. */
export function readSavedBranch() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBranch') : null;
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
